import { Component, computed, EventEmitter, Input, OnChanges, OnInit, Output, signal } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { IMPORTS } from './_imports';
import { ConfigSchema } from "src/app/models/config-schema.model";
import { Papel, Processo } from "src/app/models/edocs.model";
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceState, FlowObjectType } from "src/app/models/flow-object.model";
import { FlowDefinition, FlowInstance, FlowInstanceState } from "src/app/models/flow.model";
import { InputDataTaskAddToProcess, InputDataTaskForm, OutputDataTaskAddToProcess } from "src/app/models/input-output-data.model";
import { AuthService } from "src/app/services/auth.service";
import { EDocsService } from "src/app/services/edocs.service";
import { Enums } from "src/app/shared/enums";
import { Utils } from "src/app/shared/utils";
import { NgxSpinnerService } from 'ngx-spinner';
import { FlowObjectDefinitionService } from '../../../services/flow-object-definition.service';

@Component({
    selector: 'fob-add-to-process',
    templateUrl: './fob-add-to-process.component.html',
    styleUrl: './fob-add-to-process.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectAddToProcessComponent implements OnInit, OnChanges {
    // #region [Type props]
    FlowObjectInstanceState = FlowObjectInstanceState;
    FlowObjectType = FlowObjectType;
    Utils = Utils;
    Enums = Enums;
    // #endregion

    // #region [props]
    outputData: OutputDataTaskAddToProcess = null;
    // #endregion

    // #region [const]
    readonly PROCESS_MASK_REGEX = /^\d{4}-[BCDFGHJKLMNPQRSTVWXZbcdfghjklmnpqrstvwxz0-9]{5}$/;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectInstance);
    $flowInstance = signal(null as FlowInstance);
    $inputData = signal(null as InputDataTaskAddToProcess);
    $configSchema = signal(null as ConfigSchema);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $papeisSelector = signal([] as Papel[]);
    $processProtocol = signal(Enums.Messages.DefaultEmptyValue as string);
    $publicMessageHtml = signal(null as string);
    $process = signal(null as Processo);
    $currentLocation = signal('');
    $custodyHolder = signal(Enums.Messages.DefaultEmptyValue as string);
    $previousDocuments = signal([] as any[]);
    $inputProcessProtocol = signal('');
    $canAddToProcess = signal(false);
    $hasFoundProcess = signal(null as boolean);
    $summaryHasNoTagsLeft = signal(false);
    $isBootstrapFinished = signal(false);
    $isProcessInvalidStatus = signal(false);
    $processProtocolFromForm = signal('');
    $processProtocolFormFieldName = signal('');
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $isPublicMessageHtmlLoading = computed(() => this.$publicMessageHtml() == null);
    $hasPendingTask = computed(() => this.$model()?.stateId == FlowObjectInstanceState.Started);
    $isReadOnly = computed(() => [FlowObjectInstanceState.Finished, FlowObjectInstanceState.NotStarted].includes(this.$model()?.stateId) || this.$flowInstance()?.stateId === FlowInstanceState.CancelledByDeliberation);
    $dateString = computed(() => this.$model() == null ? '' : new Date(this.$model().updateDate).toLocaleString());
    $isProcessProtocolValid = computed(() => Utils.isNotNullOrEmpty(this.$inputProcessProtocol()) && this.PROCESS_MASK_REGEX.test(this.$inputProcessProtocol()));
    $shouldLetPublicAgentInteract = computed(() =>
        this.$currentUser()?.isPublicAgent
        && this.$model()?.flowObjectInstanceActors.some(x => x.actorId == this.$currentUser()?.id)
        && (
            this.$flowInstance()?.ownerId != this.$currentUser()?.id
            || this.$currentUser()?.papeis?.some(x =>
                x.isCurrentUserHeadOfThisRolesOrganization
                && x.lotacaoGuid.trim().toLowerCase() == this.$flowInstance()?.currentActionableLocationId.trim().toLowerCase()
            )
        )
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectInstance;
    @Input() _flowInstance: FlowInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _submitEvent = new EventEmitter<FlowObjectInstance>();
    @Output() _cancelFlowInstanceEvent = new EventEmitter<boolean>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private spinner: NgxSpinnerService,
        private cookieService: CookieService,
        private authService: AuthService,
        private eDocsService: EDocsService,
        private flowObjectDefinitionService: FlowObjectDefinitionService,
    ) { }

    async ngOnInit() {
        Utils.setSignal(this.$flowObjectDefinition, this._flowDefinition.flowObjectDefinitions.find(x => x.id == this.$model().flowObjectDefinitionId));
        this.$inputData.set(Utils.isNotNullOrEmpty(this.$model().inputData)
            ? JSON.parse(this.$model().inputData) as InputDataTaskAddToProcess
            : new InputDataTaskAddToProcess()
        );
        this.outputData = Utils.isNotNullOrEmpty(this.$model().outputData)
            ? JSON.parse(this.$model().outputData) as OutputDataTaskAddToProcess
            : new OutputDataTaskAddToProcess();
        this.$configSchema.set(JSON.parse(this.$model().flowObjectDefinition.configSchema) as ConfigSchema);

        // #region [seletor de papéis]
        if (this.$shouldLetPublicAgentInteract()) {
            Utils.setSignal(this.$papeisSelector, this.$currentUser().papeis);

            setTimeout(() => Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = null;
                return x;
            }), 50);
        }

        if (this.$papeisSelector().length == 0) {
            Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = this.$currentUser().id;
                return x;
            });
        }
        // #endregion

        // #region [Encaminhamento]
        const response_Encaminhamento = await this.eDocsService.getEncaminhamento(this.$inputData().eDocsData.forwardingId);

        if (!response_Encaminhamento.isSuccess) {
            this.toastr.error(response_Encaminhamento.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        const encaminhamento = response_Encaminhamento.data;
        // #endregion

        // #region [documentos do Encaminhamento]
        const docs = encaminhamento.documentosEncaminhamento.filter(x => !x.controle);
        for (let item of docs) {
            if (Utils.isNotNullOrEmpty(this.outputData) && this.outputData.eDocsData.forwardingId == item.documento.encaminhamentoId) {
                break;
            }
            this.addDocumentToPreviousList(item.documento);
        }

        if (Utils.isNotNullOrEmpty(this.outputData)) {
            let filteredDocs = docs.filter(x => x.documento.encaminhamentoId == this.outputData.eDocsData.forwardingId);
            for (let item of filteredDocs) {
                this.addDocumentToPreviousList(item.documento);
            }
        }
        // #endregion

        // #region [Processo]
        const processOrigin = this.$configSchema()?.taskAddToProcess?.processOrigin;

        if (!this.$hasPendingTask()) {
            await this.loadProcessData(this.outputData.eDocsData.processId);
        } else if (processOrigin == Enums.ProcessOrigin.Instance) {
            const processId = this.$inputData().eDocsData.processId;
            if (Utils.isNotNullOrEmpty(processId)) {
                await this.loadProcessData(processId);
            }
        } else if (processOrigin == Enums.ProcessOrigin.PublicAgentDeliberation) {
            this.$inputProcessProtocol.set(this.$configSchema()?.taskAddToProcess?.processProtocol);
        } else if (processOrigin == Enums.ProcessOrigin.Form) {
            const formData = this.getFormData();
            const fieldKey = this.$configSchema()?.taskAddToProcess?.processProtocolFormField;

            if (Utils.isNullOrEmpty(formData)) {
                this.toastr.error(Enums.Messages.FormDataLoadError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            if (Utils.isNullOrEmpty(fieldKey)) {
                this.toastr.error(Enums.Messages.FieldKeyLoadError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            let formSchema = JSON.parse(await this.getFormSchema() || '{}');
            this.$processProtocolFormFieldName.set(formSchema.components?.find(x => x.key == fieldKey)?.label || '(N/D)');

            this.$processProtocolFromForm.set(formData[fieldKey] || '(N/D)');

            if (this.PROCESS_MASK_REGEX.test(this.$processProtocolFromForm())) {
                const id = await this.getProcessIdFromProtocol(this.$processProtocolFromForm());
                if (Utils.isNotNullOrEmpty(id)) {
                    await this.loadProcessData(id);
                } else {
                    this.$canAddToProcess.set(false);
                    this.$process.set(null);
                    this.$processProtocol.set(null);
                    this.$custodyHolder.set(null);
                    this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
                }
            } else {
                this.$canAddToProcess.set(false);
                this.$process.set(null);
                this.$processProtocol.set(null);
                this.$custodyHolder.set(null);
                this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
            }
        }
        // #endregion

        // #region [mensagem de entranhamento no Processo]
        this.resolveAddToProcessSummary();
        // #endregion

        // #region [localização atual]
        let lastForwardingTask = this.$flowInstance().flowObjectInstances
            .filter(x => x.flowObjectDefinition.typeId == FlowObjectType.TaskForward)
            .sort((a, b) => new Date(b.finishDate).toISOString().localeCompare(new Date(a.finishDate).toISOString()))[0];

        if (lastForwardingTask != null) {
            this.$currentLocation.set(JSON.parse(lastForwardingTask.inputData)?.recipientName || Enums.Messages.DefaultEmptyValue);
        }
        // #endregion

        // #region [mensagem pública customizada]
        if (Utils.isNotNullOrEmpty(this.$flowObjectDefinition()?.publicMessageHtml)) {
            this.$publicMessageHtml.set(Utils.getParsedMessageHtml(
                this.cookieService,
                this.$flowObjectDefinition().publicMessageHtml,
                this.$flowInstance().rootForwardingInfo,
                this.$processProtocol()
            ));
        }
        // #endregion

        setTimeout(() => this.$isBootstrapFinished.set(true), 750);
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowInstance.set(this._flowInstance);
    }

    goToProcess() {
        const resolvedSubdomain = Utils.getResolvedEDocsSubdomain(this.cookieService);
        window.open(`https://${resolvedSubdomain}es.gov.br/p/${this.$processProtocol()}`, '_blank');
    }

    signerIdChange(event) {
        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = event;
            return x;
        });
    }

    async submit() {
        const processOrigin = this.$configSchema()?.taskAddToProcess?.processOrigin;

        // Verifica se o Papel foi selecionado antes de enviar os dados
        if (!this.$inputData().eDocsData.signerId) {
            this.toastr.warning(Enums.Messages.NoSignerIdError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return;
        }

        // Verifica se o protocolo do processo é válido antes de enviar os dados
        if (processOrigin == Enums.ProcessOrigin.PublicAgentDeliberation && this.$hasPendingTask()) {
            if (!this.PROCESS_MASK_REGEX.test(this.$inputProcessProtocol())) {
                this.toastr.warning(Enums.Messages.ProcessNotFoundError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
                this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
                return;
            }
        }

        let protocol = '';
        if (processOrigin == Enums.ProcessOrigin.PublicAgentDeliberation) {
            protocol = await this.getProcessIdFromProtocol(this.$inputProcessProtocol());
            if (Utils.isNullOrEmpty(protocol)) {
                this.toastr.warning(Enums.Messages.ProcessNotFoundError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
                this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
                return;
            }
        }

        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.processId = this.$process().id;
            x.eDocsData.documentsIds = this.$previousDocuments().map(d => d.id);

            if (processOrigin == Enums.ProcessOrigin.PublicAgentDeliberation) {
                x.eDocsData.processProtocol = protocol;
            }

            return x;
        });

        Utils.updSignal(this.$model, x => {
            x.inputData = JSON.stringify(this.$inputData());
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    async checkProcess(processProtocol: string) {
        if (!this.$isProcessProtocolValid()) return;

        processProtocol = processProtocol.toUpperCase();

        const id = await this.getProcessIdFromProtocol(processProtocol);
        if (Utils.isNotNullOrEmpty(id)) {
            await this.loadProcessData(id);
        } else {
            this.$canAddToProcess.set(false);
            this.$process.set(null);
            this.$processProtocol.set(null);
            this.$custodyHolder.set(null);
        }
    }

    getAddToProcessSummary() {
        return this.$configSchema()?.taskAddToProcess?.addToProcessSummary?.replaceAll('\n', '<br>');
    }

    /* ====================== */
    // private methods
    /* ====================== */

    private async loadProcessData(processId: string) {
        if (Utils.isNullOrEmpty(processId)) return;

        this.spinner.show('flowInstance');

        const response = await this.eDocsService.getProcesso(processId);
        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
            return;
        }

        Utils.setSignal(this.$process, response.data);
        this.$processProtocol.set(this.$process().protocolo);

        if (
            this.$process().situacao != Enums.ProcessStatus.EmAndamento.toString()
            && this.$process().situacao != Enums.ProcessStatusLabel.EmAndamento
        ) {
            this.$canAddToProcess.set(false);
            this.$isProcessInvalidStatus.set(true);
            this._cancelFlowInstanceEvent.emit(this.$shouldLetPublicAgentInteract());
        } else {
            this.$canAddToProcess.set(true);
        }

        const response_LocalCustodia = await this.eDocsService.getProcessoLocalCustodia(response.data?.id || processId);

        if (response_LocalCustodia.isSuccess) {
            const localCustodia = response_LocalCustodia.data;
            this.$custodyHolder.set(localCustodia.nome);
        }

        this.spinner.hide('flowInstance');
    }

    private async getProcessIdFromProtocol(processProtocol: string) {
        this.spinner.show('flowInstance');

        const response = await this.eDocsService.postProcessoPaginatedSearch({ protocolos: [processProtocol] });
        if (!response.isSuccess) {
            if (!response.message.description.includes('AAAA-XXXXX')) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            }

            this.$hasFoundProcess.set(false);
            this.spinner.hide('flowInstance');
            return;
        }

        const id = response.data.result.map(x => x.id)[0];
        if (Utils.isNullOrEmpty(id)) {
            this.$hasFoundProcess.set(false);
        } else {
            this.$hasFoundProcess.set(true);
        }

        this.spinner.hide('flowInstance');
        return id;
    }

    private addDocumentToPreviousList(doc: any) {
        if (!this.$previousDocuments().some(x => x.registro == doc.registro)) {
            Utils.updSignal(this.$previousDocuments, x => {
                x.push({
                    id: doc.id,
                    registro: doc.registro,
                    filename: `${doc.nome}.${doc.extensao}`
                });
                return x;
            });
        }
    }

    private getFormData(): any {
        let formTaskDefinition = this._flowDefinition.flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm);
        if (formTaskDefinition == null) return null;

        let formTaskInstance = this.$flowInstance().flowObjectInstances.find(x => x.flowObjectDefinitionId == formTaskDefinition.id);
        if (formTaskInstance == null) return null;

        let formData = JSON.parse(formTaskInstance.inputData) as InputDataTaskForm;

        return formData.data;
    }

    private async getFormSchema() {
        const response = await this.flowObjectDefinitionService.getFormSchema(this._flowDefinition.flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm).id);

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return null;
        }

        return response.data;
    }

    private resolveAddToProcessSummary() {
        let formData = this.getFormData();
        if (formData == null) return;

        let tags = this.$configSchema().taskAddToProcess.addToProcessSummary.match(/\{\|([\w]+)\|\}/g);
        if (tags?.length > 0) {
            let formFields = tags.map(x => x.replace(/[^\w]/g, ''));

            for (let i = 0; i < tags.length; i++) {
                Utils.updSignal(this.$configSchema, x => {
                    x.taskAddToProcess.addToProcessSummary = x.taskAddToProcess.addToProcessSummary.replaceAll(
                        tags[i],
                        formData[formFields[i]] || Enums.Messages.DefaultEmptyValue
                    );
                    return x;
                });
            }
        }

        if (this.$configSchema().taskAddToProcess.addToProcessSummary.match(/\{\|([\w]+)\|\}/g)?.length > 0) {
            setTimeout(() => this.resolveAddToProcessSummary(), 200);
        } else {
            this.$summaryHasNoTagsLeft.set(true);
        }
    }
}
